<script setup lang="ts">
import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui'
import type { Client } from '~/types/client.type'
import type { MappedOmit } from '~/types/common.type'
import type { Project } from '~/types/project.type'
import type { Report } from '~/types/report.type'
import type { Task } from '~/types/task.type'
import type { TaskList } from '~/types/taskList.type'
import { copyFeatureNotSupportedHint, unknownError } from '~/constants'

const props = defineProps({
  showClients: {
    type: Boolean,
    default: true,
  },
  showProjects: {
    type: Boolean,
    default: true,
  },
  showTaskLists: {
    type: Boolean,
    default: true,
  },
  showTasks: {
    type: Boolean,
    default: true,
  },
  showReports: {
    type: Boolean,
    default: true,
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  disableShortcutInModals: {
    type: Boolean,
    default: true,
  },
  includeCompletedAndArchivedResources: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits<{
  taskSelected: [task: Task<'withTaskList'>]
  taskListSelected: [taskList: TaskList<'withProject'>]
  projectSelected: [project: Project<'withClient'>]
  clientSelected: [client: Client]
  reportSelected: [report: Report]
}>()

const { can } = useAppAbility()

const searchInput = ref('')
const searchTerm = debouncedRef(searchInput, 300)

const {
  execute: performSearch,
  data: globalSearchData,
  error: globalSearchError,
  status: globalSearchStatus,
} = useApiGlobalSearch(computed<ApiGlobalSearchParams>(() => ({
  search: searchTerm.value,
  withCompleted: can('read', 'global-search', 'withCompleted') && props.includeCompletedAndArchivedResources,
  searchLimit: 5,
})), { immediate: false, watch: false })

watch(searchTerm, () => performSearch())

const toast = useToast()

watch(globalSearchError, (error) => {
  if (error) {
    toast.add({ color: 'error', description: error.data?.message ?? unknownError })
  }
})

const labels = {
  tasks: 'Aufgaben',
  taskLists: 'Aufgabenlisten',
  projects: 'Projekte',
  clients: 'Kunden',
  reports: 'Zeitberichte',
}

type CustomCommandPaletteItem = (CommandPaletteItem & { children: Array<CommandPaletteItem & { element: ApiGlobalSearchResults[keyof ApiGlobalSearchResults][number] }> })
  | (CommandPaletteItem & { element: ApiGlobalSearchResults[keyof ApiGlobalSearchResults][number] })

type CustomCommandPaletteGroup = MappedOmit<
  CommandPaletteGroup<CustomCommandPaletteItem>,
  'postFilter'
> & { postFilter?: (searchTerm: string, items: any[]) => CommandPaletteItem[] }

const isCommandPaletteOpen = defineModel('show', {
  type: Boolean,
  default: false,
})

const recentTasks = useRecentTasks()

const groups = computed(() => {
  const groups: CustomCommandPaletteGroup[] = []

  if (!globalSearchData.value?.data || searchTerm.value === '') {
    groups.push({
      id: 'recent-tasks',
      label: 'Letzte 5 Aufgaben',
      ignoreFilter: true,
      items: recentTasks.value.tasks.map(task => ({
        element: task,
        slot: 'tasks',
        label: task.name,
        onSelect() {
          isCommandPaletteOpen.value = false
          if (props.selectMode) {
            emit('taskSelected', task)
          }
          else {
            navigateTo({ name: 'tasks-id', params: { id: task.id } })
          }
        },
      })),
    })

    return groups
  }

  for (const key in globalSearchData.value.data) {
    const group: (typeof groups)[number] = {
      id: key,
      label: labels[key as keyof typeof labels],
      items: [],
      ignoreFilter: true,
    }

    if ((key === 'clients' && (!can('read', 'clients') || !props.showClients)))
      continue

    if ((key === 'projects' && (!can('read', 'projects') || !props.showProjects)))
      continue

    if ((key === 'taskLists' && (!can('read', 'task-lists-id') || !props.showTaskLists)))
      continue

    if ((key === 'tasks' && (!can('read', 'tasks-id') || !props.showTasks)))
      continue

    if ((key === 'reports' && (!can('read', 'reports') || !props.showReports)))
      continue

    group.items = globalSearchData.value.data[key as keyof ApiGlobalSearchResults].map((element) => {
      return {
        element,
        slot: key,
        label: element.name,
        onSelect() {
          isCommandPaletteOpen.value = false

          if (props.selectMode) {
            switch (key) {
              case 'clients':
                emit('clientSelected', element as Client)
                break
              case 'projects':
                emit('projectSelected', element as Project<'withClient'>)
                break
              case 'taskLists':
                emit('taskListSelected', element as TaskList<'withProject'>)
                break
              case 'tasks':
                emit('taskSelected', element as Task<'withTaskList'>)
                break
              case 'reports':
                emit('reportSelected', element as Report)
                break
            }
            return
          }

          switch (key) {
            case 'clients':
              navigateTo({ name: 'clients-id-view', params: { id: element.id, view: 'projects' } })
              break
            case 'projects':
              navigateTo({ name: 'projects-id-view', params: { id: element.id, view: 'tasks' } })
              break
            case 'taskLists':
              navigateTo({ name: 'task-lists-id', params: { id: element.id } })
              break
            case 'tasks':
              navigateTo({ name: 'tasks-id', params: { id: element.id } })
              break
            case 'reports':
              navigateTo({ name: 'reports-id-view', params: { id: element.id, view: 'overview' } })
              break
          }
        },
      }
    })

    // TODO: add items with children once they controls are more flexible
    // if (items.length > 5) {
    //   group.items!.push({
    //     label: `${items.length} ${labels[key as keyof typeof labels]}`,
    //     children: items,
    //     slot: 'item-group',
    //   })
    // }
    // else {
    //   group.items = items
    // }

    groups.push(group)
  }

  return groups
})

const clipboardStore = useClipboardStore()

const { canWriteToClipboard } = storeToRefs(clipboardStore)

const { overlays } = useOverlay()

const isAnyOverlayOpen = computed(() => {
  return overlays.some(overlay => overlay.isOpen)
})

defineShortcuts({
  '/': () => {
    if (props.disableShortcutInModals && isAnyOverlayOpen.value)
      return
    isCommandPaletteOpen.value = !isCommandPaletteOpen.value
  },
})
</script>

<template>
  <UModal v-model:open="isCommandPaletteOpen">
    <slot name="trigger">
      <NTooltip placement="bottom">
        <template #trigger>
          <NInput
            placeholder="Alles durchsuchen..."
            readonly
            @click="isCommandPaletteOpen = true"
          >
            <template #prefix>
              <UIcon name="i-ic-baseline-search" />
            </template>
          </NInput>
        </template>
        Tippe <UKbd>/</UKbd> für die Suche
      </NTooltip>
    </slot>

    <template #content>
      <UCommandPalette
        v-model:search-term="searchInput"
        :loading="globalSearchStatus === 'pending'"
        :groups="groups"
        placeholder="Alles durchsuchen..."
        class="h-80"
      >
        <template #empty>
          <div v-show="!searchInput">
            Hier kannst du trackIT durchsuchen, um beispielsweise ein Projekt oder eine Aufgabe zu finden.
          </div>
          <div v-show="!!searchInput">
            Keine Suchergebnisse gefunden :(
          </div>
        </template>
        <template #projects-label="{ item }">
          <NTooltip>
            <template #trigger>
              <div class="truncate">
                <span class="text-highlighted" :class="{ 'line-through': !(item.element as Project).isActive }">{{ item.label }}</span>
                <span class="text-xs">
                  ({{ item.element.client.name }})
                </span>
              </div>
            </template>
            <div>
              {{ item.label }}
              <span v-if="!(item.element as Project).isActive">(Abgeschlossen)</span>
            </div>
          </NTooltip>
        </template>
        <template #projects-trailing="{ item }">
          <NButtonGroup>
            <NTooltip>
              <template #trigger>
                <NButton size="small" :disabled="!canWriteToClipboard" @click.stop="clipboardStore.copyProject(item.element as Project)">
                  <template #icon>
                    <UIcon class="text-xs" :name="clipboardStore.isProjectCopied(item.element.id).value ? 'i-ic-outline-library-add-check' : 'i-ic-baseline-content-copy'" />
                  </template>
                </NButton>
              </template>
              <span v-if="canWriteToClipboard">Projekt kopieren</span>
              <span v-else>{{ copyFeatureNotSupportedHint }}</span>
            </NTooltip>
          </NButtonGroup>
        </template>
        <template #taskLists-label="{ item }">
          <NTooltip>
            <template #trigger>
              <div class="truncate">
                <span class="text-highlighted" :class="{ 'line-through': (item.element as TaskList).isCompleted }">{{ item.label }}</span>
                <span class="text-xs" :class="{ 'line-through': !(item.element as TaskList).project?.isActive }">
                  ({{ item.element.project.name }})
                </span>
              </div>
            </template>
            <div>
              {{ item.label }}
              <span v-if="(item.element as TaskList).isCompleted">(Abgeschlossen)</span>
            </div>
          </NTooltip>
        </template>
        <template #taskLists-trailing="{ item }">
          <NButtonGroup>
            <NTooltip>
              <template #trigger>
                <NButton size="small" :disabled="!canWriteToClipboard" @click.stop="clipboardStore.copyTaskList(item.element as TaskList)">
                  <template #icon>
                    <UIcon class="text-xs" :name="clipboardStore.isTaskListCopied(item.element.id).value ? 'i-ic-outline-library-add-check' : 'i-ic-baseline-content-copy'" />
                  </template>
                </NButton>
              </template>
              <span v-if="canWriteToClipboard">Aufgabenliste kopieren</span>
              <span v-else>{{ copyFeatureNotSupportedHint }}</span>
            </NTooltip>
          </NButtonGroup>
        </template>
        <template #tasks-label="{ item }">
          <NTooltip>
            <template #trigger>
              <div class="truncate">
                <span class="text-highlighted" :class="{ 'line-through': (item.element as Task).isCompleted }">{{ item.label }}</span>
                <span class="text-xs">
                  (<span :class="{ 'line-through': !(item.element as Task).taskList.project?.isActive }">
                    {{ item.element.taskList.project.name }}
                  </span>/<span :class="{ 'line-through': (item.element as Task).taskList.isCompleted }">{{ item.element.taskList.name }}</span>)
                </span>
              </div>
            </template>
            <div>
              {{ item.label }}
              <span v-if="(item.element as Task).isCompleted">(Abgeschlossen)</span>
            </div>
          </NTooltip>
        </template>
        <template #tasks-trailing="{ item }">
          <NButtonGroup>
            <TimeTrackerButton v-if="!selectMode" :task-id="item.element.id" :button-props="{ size: 'small' }" />
            <TimeEntryButton v-if="!selectMode" :project-id="item.element.taskList.projectId ?? 0" :task-id="item.element.id" :button-props="{ size: 'small' }" />
            <NTooltip>
              <template #trigger>
                <NButton size="small" :disabled="!canWriteToClipboard" @click.stop="clipboardStore.copyTask(item.element as Task)">
                  <template #icon>
                    <UIcon class="text-xs" :name="clipboardStore.isTaskCopied(item.element.id).value ? 'i-ic-outline-library-add-check' : 'i-ic-baseline-content-copy'" />
                  </template>
                </NButton>
              </template>
              <span v-if="canWriteToClipboard">Aufgabe kopieren</span>
              <span v-else>{{ copyFeatureNotSupportedHint }}</span>
            </NTooltip>
          </NButtonGroup>
        </template>
        <template #item-group-label="{ item }">
          <span class="text-gray-600 dark:text-gray-300 italic">{{ item.label }}</span>
        </template>
      </UCommandPalette>
    </template>
  </UModal>
</template>
